import React from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Props for the Rating component
 */
interface RatingProps {
  /** The current rating value (can be decimal, e.g., 4.5) */
  value: number;
  /** Maximum rating value. Defaults to 5. */
  max?: number;
  /** Whether to display the numeric rating value. Defaults to true. */
  showValue?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Rating component displays a star-based rating with support for partial stars.
 * Shows filled, partially filled, and empty stars based on the rating value.
 * 
 * @component
 * @param {RatingProps} props - Component props
 * @param {number} props.value - Current rating value (supports decimals)
 * @param {number} [props.max=5] - Maximum rating value
 * @param {boolean} [props.showValue=true] - Whether to show numeric value
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Star rating display with optional numeric value
 * 
 * @example
 * ```tsx
 * <Rating value={4.5} max={5} />
 * ```
 * 
 * @example
 * ```tsx
 * <Rating value={3} showValue={false} />
 * ```
 */
export const Rating: React.FC<RatingProps> = ({
  value,
  max = 5,
  showValue = true,
  className,
}) => {
  const fullStars = Math.floor(value);
  const decimalPart = value % 1;
  const hasPartialStar = decimalPart > 0;
  const partialStarWidth = decimalPart * 100;
  const emptyStars = max - fullStars - (hasPartialStar ? 1 : 0);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {showValue && (
        <span className="text-sm text-foreground text-[#3D424F]">
          <span className="pl-5">{value.toFixed(1)} / {max}</span>
        </span>
      )}
      <div className="flex items-center gap-0.5">
        {Array.from({ length: fullStars }).map((_, i) => (
          <Star
            key={`full-${i}`}
            className="h-4 w-4 fill-[#FF7518] text-[#FF7518]"
          />
        ))}
        {hasPartialStar && (
          <div className="relative h-4 w-4">
            <Star className="absolute h-4 w-4 text-[#FF7518]" />
            <div 
              className="absolute overflow-hidden" 
              style={{ width: `${partialStarWidth}%`, height: "100%" }}
            >
              <Star className="h-4 w-4 fill-[#FF7518] text-[#FF7518]" />
            </div>
          </div>
        )}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <Star
            key={`empty-${i}`}
            className="h-4 w-4 text-[#FF7518]"
          />
        ))}
      </div>
    </div>
  );
};

